import { type ReactNode } from "react";
import { useRoleRequest } from "@fox1ck-org/auth/ui";
import { RoleLock } from "@fox1ck-org/ui/saas";

import { useCaller, useTrackerRoles, type TrackerRoles } from "@/lib/permissions";

/**
 * What a gate needs, as the catalog role that opens it: `write` — «Байер»
 * (tracker-member, RequireWrite), `manage` — «Админ» (RequireManage: the
 * workspace's settings, integrations, antifraud, removing a domain).
 */
const NEEDS = { write: "buyer", manage: "admin" } as const;
export type RoleNeed = keyof typeof NEEDS;

export function allows(roles: TrackerRoles, need: RoleNeed): boolean {
  return need === "manage" ? roles.canManage : roles.canWrite;
}

export interface RoleGateProps {
  /** What the gated thing needs. */
  need: RoleNeed;
  /** Drawn when the role allows it. May be empty — the gate then only draws the lock. */
  children?: ReactNode;
  /** What is locked — the lock's title («Настройки рабочего пространства»). */
  title?: string;
  /** `inline` (default) — the control itself, dimmed; `panel` — a card; `page` — the whole area. */
  variant?: "inline" | "panel" | "page";
  /** `inline`: the control's own label («Новый оффер»). */
  label?: ReactNode;
  className?: string;
}

/**
 * A tracker action the caller's role does not open is drawn, never hidden
 * (a refusal is never a dead end): `@fox1ck-org/ui/saas` RoleLock — «Нужна
 * роль «Админ»», what it gives, the role held now and «Запросить доступ»
 * (console RequestRole; owners and admins grant it in «Участники»). The role
 * check is the API's (GetCaller); the words are the catalog's.
 */
export function RoleGate({ need, children = null, ...lock }: RoleGateProps) {
  const caller = useCaller();
  const roles = useTrackerRoles();
  if (caller.isPending) return null;
  if (allows(roles, need)) return <>{children}</>;
  return <Locked need={need} {...lock} />;
}

/** The lock itself — console is asked only when something is locked. */
function Locked({ need, title, variant = "inline", label, className }: Omit<RoleGateProps, "children">) {
  const access = useRoleRequest();
  return (
    <RoleLock {...access.lock(NEEDS[need])} variant={variant} {...(title ? { title } : {})} {...(className ? { className } : {})}>
      {label}
    </RoleLock>
  );
}
